import { AfterViewInit, Component, computed, inject } from '@angular/core';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { Observable } from 'rxjs';
import { Level, Path } from '../../services/data-tree';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { att, percOfRow } from '../../services/measures';
import { nth, sum } from 'lodash-es';
import { maxOverMapped, sumIf } from '../../services/aggregation';
import { delay } from 'rxjs/operators';
import { DRIE_SCHOOLJAREN_EXCL_2020, getOnderwijsresultatenSchooljaarRangeString } from '@cumlaude/shared-utils';
import { onderwijspositie_0_5_10 } from '../../services/onderwijspositie';
import { BarInfo } from '../../services/stacked-bars';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, getLevelFilters, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Router } from '@angular/router';
import { DashboardVariant } from '../../services/weergave-opties';
import { indicatorOverOpties, SchooljaarIndicatorOverComponent } from '../../schooljaar-indicator-over/schooljaar-indicator-over.component';
import { TooltipDirective, TooltipElement } from '@cumlaude/shared-components-overlays';
import { formatNumber } from '@angular/common';
import { PsName } from '../../services/page-state.service';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { OnderwijsresultatenUitsluitenFilterComponent } from '../../onderwijsresultaten-uitsluiten-filter/onderwijsresultaten-uitsluiten-filter.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { AttrPath, Table } from '@cumlaude/metadata';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface OnderwijspositieI extends Attributes {
	ds_fun_co_verschil_basisschooladvies_naar: string | null;
	ds_is_prognose: number;
	ds_m_nr_weging: number;
}

interface OnderwijspositieA extends Attributes {
	verschil_weging: number;
	onbekend: number;
	bekend: number;
	bekend_lln: number;
	ds_is_prognose: number;
}

const basisschoolAdvies: AttrPath = ['ds_fk_ll', 'll_nm_basisschooladvies'];
const basisschool: AttrPath = ['ds_fk_ll', 'll_nm_svh'];
const plaatsingLeerjaar3: AttrPath = ['ds_fun_nm_niveau_volgend_sj'];

// aantal leerlingen voor het betreffende schooljaar voor de betreffende bekostigingings-BRIN moet >= 20 zijn
const uitsluitenFilter = new BasicFilterExpression(['ds_fk_or_opcl', 'or_q_leerlingen'], 20, '>=');

const permanentFilters: FilterExpression[] = [
	new BasicFilterExpression(['ds_nr_leerjaar_van'], 2),
	new BasicFilterExpression(['ds_is_doublure'], 0),
	new BasicFilterExpression(['ds_is_relevante_doorstroom'], 1),
	new BasicFilterExpression(['ds_fk_br_vest_van', 'br_is_vso'], 0),
	new BasicFilterExpression(['ds_fk_lb_van', 'lb_is_pl_voorlopig'], 0),
	new BasicFilterExpression(['ds_nr_weging'], 0, '>'),
];

export const onderwijspositieFilters: FilterExpression[] = [...permanentFilters, uitsluitenFilter];

@Component({
	selector: 'app-onderwijspositie',
	templateUrl: './onderwijspositie.component.html',
	styleUrls: ['./onderwijspositie.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		TooltipDirective,
		SchooljaarIndicatorOverComponent,
		OnderwijsresultatenUitsluitenFilterComponent,
		BarchartTableComponent,
		FilterBarComponent,
		IconComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class OnderwijspositieComponent extends BarchartTableConfig<OnderwijspositieI, OnderwijspositieA> implements AfterViewInit {
	protected readonly router = inject(Router);
	private readonly dataService = inject(DataService);

	protected readonly DRIE_SCHOOLJAREN_EXCL_2020 = DRIE_SCHOOLJAREN_EXCL_2020;

	initialFilters: FilterName[] = [
		'x_onderwijsresultaten_ds_schooljaar', //
		'ds_fk_br_vest_van.br_co_brin',
		'ds_nm_op_uitzondering_van',
	];

	readonly groups = computed(() => {
		const groups = [...this.groeperingService.huidigeGroeperingen()];
		if (this.variant() === DashboardVariant.HISTORIE) groups.push(['ds_nm_schooljaar_van']);
		return groups;
	});

	readonly subgroups = computed(() => {
		const verschilSubgroups: AttrPath[] = [['ds_fun_co_verschil_basisschooladvies_naar']];

		const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
		if (huidigeGroeperingen.length == 1) {
			const selectedGroup = huidigeGroeperingen[0].join('.');
			if (selectedGroup === plaatsingLeerjaar3.join('.')) return [...verschilSubgroups, basisschoolAdvies, ['ds_is_prognose']] as AttrPath[];

			if (selectedGroup === basisschoolAdvies.join('.'))
				return [...verschilSubgroups, ['ds_fun_nm_niveau_volgend_sj'], ['ds_is_prognose']] as AttrPath[];

			if (selectedGroup === basisschool.join('.')) return [...verschilSubgroups, ['ds_is_prognose']] as AttrPath[];
		}

		return [...verschilSubgroups, ['ds_is_prognose'] as AttrPath];
	});

	aantalJaren!: number;

	schooljaar?: string;

	variant = this.qp.signal('variant');

	uitsluiten = this.qp.signal('or-uitsluiten');

	readonly permanentExpressions = computed(() => (this.uitsluiten() ? [...permanentFilters, uitsluitenFilter] : permanentFilters));

	dashboardName!: string;

	constructor() {
		super();

		this.subscriptions.push(this.urlService.routeData$.subscribe((routeData) => (this.dashboardName = routeData.title)));
	}

	override ngAfterViewInit() {
		super.ngAfterViewInit();
		this.subscriptions.push(
			this.filterService
				.observe('x_onderwijsresultaten_ds_schooljaar')
				.pipe(delay(0))
				.subscribe(([schooljaar, aantalJaren]) => {
					this.schooljaar = schooljaar;
					this.aantalJaren = aantalJaren;
				})
		);
	}

	protected override getFixedAfterGroups(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 0 : 1;
	}

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData(options);
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	protected override singleAggregators = {
		verschil_weging: {
			init: ({ ds_fun_co_verschil_basisschooladvies_naar, ds_m_nr_weging }: OnderwijspositieI) =>
				(onderwijspositie_0_5_10(ds_fun_co_verschil_basisschooladvies_naar) / 10) * ds_m_nr_weging,
			combine: (as: number[]) => sum(as),
		},
		bekend: sumIf<'ds_m_nr_weging', OnderwijspositieI>(
			'ds_m_nr_weging',
			({ ds_fun_co_verschil_basisschooladvies_naar }: OnderwijspositieI) => ds_fun_co_verschil_basisschooladvies_naar !== null
		),
		bekend_lln: sumIf<'count_records', OnderwijspositieI>(
			'count_records',
			({ ds_fun_co_verschil_basisschooladvies_naar }: OnderwijspositieI) => ds_fun_co_verschil_basisschooladvies_naar !== null
		),
		onbekend: sumIf<'ds_m_nr_weging', OnderwijspositieI>(
			'ds_m_nr_weging',
			({ ds_fun_co_verschil_basisschooladvies_naar }: OnderwijspositieI) => ds_fun_co_verschil_basisschooladvies_naar === null
		),
		ds_is_prognose: maxOverMapped<OnderwijspositieI>((v) => Number(v.ds_is_prognose)),
	};

	override makeBar(
		attrs: OnderwijspositieI,
		path: Path<OnderwijspositieA, number[]>,
		context: DashboardContext<OnderwijspositieI, OnderwijspositieA, OnderwijspositieComponent>
	): BarInfo {
		const { ds_m_nr_weging, ds_is_prognose, ds_fun_co_verschil_basisschooladvies_naar } = attrs;
		const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
		const huidigeGroepering = huidigeGroeperingen[0];
		const { tooltipElements, text } = this.getStackText(huidigeGroepering, path);
		const verschilpunten = onderwijspositie_0_5_10(ds_fun_co_verschil_basisschooladvies_naar);
		const tooltip: TooltipElement[] = [...tooltipElements, { label: `Aantal`, value: `${formatNumber(ds_m_nr_weging, 'nl_NL', '1.0-2')}` }];
		const kleur = verschilpunten === 5 ? '5hv' : verschilpunten;
		const prognose = Number(ds_is_prognose) ? '-prognose' : '';
		return {
			...super.makeBar(attrs, path, context),
			text,
			className: `verschil-${kleur}${prognose}`,
			tooltip,
		};
	}

	getStackText(groepering: AttrPath, path: Path<OnderwijspositieA, number[]>): { tooltipElements: TooltipElement[]; text: string } {
		const k = nth(path, -2)!.k;
		switch (groepering?.join('.')) {
			case basisschoolAdvies.join('.'):
				return { tooltipElements: [{ label: `Plaatsing leerjaar 3`, value: `${k}` }], text: this.displayService.display(k) };
			case plaatsingLeerjaar3.join('.'):
				return { tooltipElements: [{ label: `Basisschooladvies`, value: `${k}` }], text: this.displayService.display(k) };
			default:
				return { tooltipElements: [], text: '' };
		}
	}

	override partitionBarData(rowRoot: Level<OnderwijspositieA, number[]>): Path<OnderwijspositieA, number[]>[][] {
		const verschil_lvl = this.groups().length + 1;
		return [rowRoot.r.filter((path) => path[verschil_lvl].k !== null)];
	}

	override createMeasureColumns(
		context: DashboardContext<OnderwijspositieI, OnderwijspositieA, OnderwijspositieComponent>
	): ColumnDef<DataRow<OnderwijspositieA>>[] {
		return [
			createMeasureColumn('Plaatsing/advies', percOfRow('verschil_weging', 'bekend'), { dataType: 'percentage', format: '+1.2-2' }),
			this.createLeerlingColumn('Aantal', att('bekend'), { context }, att('bekend_lln'), this.getBekendFilters()),
			createMeasureColumn('Verschil onbekend', att('onbekend'), {
				clickHandler: (path) => this.handleUitzonderingRedirect(path, context),
			}),
		];
	}

	override onContextCreated(context: DashboardContext<OnderwijspositieI, OnderwijspositieA, OnderwijspositieComponent>) {
		const dataRoot = context.dataRoot;

		this.pageStateService.dispatch(PsName.prognose, String(Boolean(dataRoot?.a.ds_is_prognose)));
	}

	override getAlternatingGroupLevel(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 1 : 2;
	}

	override enrichTableModel(
		_context: DashboardContext<OnderwijspositieI, OnderwijspositieA, OnderwijspositieComponent>,
		tableModel: TableModel<DataRow<OnderwijspositieA>>
	) {
		const column = tableModel.getColumnDef('ds_fun_nm_niveau_volgend_sj');
		if (column) column.header.getValue = () => 'Plaatsing leerjaar 3';
	}

	getSchooljarenCaption() {
		return this.schooljaar ? getOnderwijsresultatenSchooljaarRangeString(this.schooljaar, this.aantalJaren, 'op') : '';
	}

	getSchooljarenTooltip() {
		if (!this.schooljaar) return '';

		return `Let op: de onderwijsinspectie toont deze periode als ${getOnderwijsresultatenSchooljaarRangeString(this.schooljaar, this.aantalJaren, 'op', true)}`;
	}

	private getBekendFilters() {
		return [new BasicFilterExpression(<AttrPath>['ds_fun_co_verschil_basisschooladvies_naar'], null, '<>')];
	}

	private handleUitzonderingRedirect(
		path: Path<OnderwijspositieA, number[]>,
		context: DashboardContext<OnderwijspositieI, OnderwijspositieA, OnderwijspositieComponent>
	) {
		const params = {
			from: this.dashboardName,
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		this.urlService.redirectDoorklik(['/details/uitzondering/onderwijspositie'], params);
	}

	override getDisplayOptions(): ExportFilter[] {
		return [
			{ label: 'Indicator over', value: indicatorOverOpties[this.aantalJaren] },
			{ label: 'Meetellende vestigingen', value: this.uitsluiten() ? 'Minimaal 20 leerlingen' : 'Alle' },
		];
	}

	override getSelectionConfig(context: DataRequestContext<OnderwijspositieA>): SelectionConfig<OnderwijspositieA> | undefined {
		return {
			...super.getSelectionConfig(context)!,
			extraFilters: [],
		};
	}

	protected readonly DashboardVariant = DashboardVariant;
}
